import segment from '../core/segment';
import { trackEventAndUpdate, EventNames } from '../lib/analytics';

interface InstrumentStyleImageMap {
  blues: string;
  country: string;
  folk: string;
  funk: string;
  pop: string;
  rock: string;
  'rock-bass': string;
  rnbsoul: string;
}

export type InstrumentStyleImageMapKeys = keyof InstrumentStyleImageMap;

const instrumentStyleImageMap: InstrumentStyleImageMap = {
  blues: 'https://stuff.fendergarage.com/images/b/I/r/onboarding_quiz-style_blues.jpg',
  country: 'https://stuff.fendergarage.com/images/3/M/g/onboarding_quiz-style_country.jpg',
  folk: 'https://stuff.fendergarage.com/images/o/k/m/onboarding_quiz-style_folk.jpg',
  funk: 'https://stuff.fendergarage.com/images/e/F/w/onboarding_quiz-style_funk-bass.jpg',
  pop: 'https://stuff.fendergarage.com/images/a/6/i/onboarding_quiz-style_pop_11302021-113654.jpg',
  rock: 'https://stuff.fendergarage.com/images/W/J/U/onboarding_quiz-style_rock-guitar.jpg',
  'rock-bass': 'https://stuff.fendergarage.com/images/B/T/7/onboarding_quiz-style_rock-bass.jpg',
  rnbsoul: 'https://stuff.fendergarage.com/images/s/e/e/RB_Soul.jpg',
};

export const parseStyle = (instrument: string, style: InstrumentStyleImageMapKeys): string =>
  style === 'rock' && instrument === 'bass' ? 'rock-bass' : style;

const getInstrumentsStyleImage = (
  instrument: string,
  style: InstrumentStyleImageMapKeys,
): string => {
  const ps = parseStyle(instrument, style);
  return instrumentStyleImageMap[ps as InstrumentStyleImageMapKeys];
};

interface InstrumentPath {
  instrument: string;
  image: string;
  title: string;
  paths: null | any[];
}

const instrumentsMap: { [key: string]: InstrumentPath } = {
  'electric-guitar': {
    instrument: 'electric-guitar',
    image:
      'https://stuff.fendergarage.com/images/4/M/S/onboarding_quiz-instrument_electric_guitar.jpg',
    title: 'Electric Guitar',
    paths: null,
  },
  'acoustic-guitar': {
    instrument: 'acoustic-guitar',
    image:
      'https://stuff.fendergarage.com/images/v/P/b/onboarding_quiz-instrument_acoustic_guitar_20210421.jpg',
    title: 'Acoustic Guitar',
    paths: null,
  },
  bass: {
    instrument: 'bass',
    image: 'https://stuff.fendergarage.com/images/K/1/N/onboarding_quiz-instrument_bass.jpg',
    title: 'Bass',
    paths: null,
  },
  ukulele: {
    instrument: 'ukulele',
    image:
      'https://stuff.fendergarage.com/images/K/0/2/onboarding_quiz-instrument_acoustic_uke_20210421.jpg',
    title: 'Ukulele',
    paths: null,
  },
};

const sortGenres = (genreList: any): void => {
  const order = ['Rock', 'Blues', 'Pop', 'R&B/Soul', 'Country', 'Folk'];
  genreList.sort((a: any, b: any) => {
    return order.indexOf(a.title) - order.indexOf(b.title);
  });
};

/**
 * Returns the instrument type.
 */
function getInstrumentType(instrument: string | null): string | null {
  if (typeof instrument === 'string') {
    const type = instrument.split('-');
    return type.length > 1 ? type[0] : null;
  }
  return null;
}

/**
 * Returns an object containting instruments with mapped paths associated
 * with each instrument.
 *
 * @param {array} pathList - list of paths
 */
function getInstruments(pathList: any) {
  if (pathList && Array.isArray(pathList)) {
    Object.keys(instrumentsMap).forEach(key => {
      instrumentsMap[key].paths = pathList.filter(
        path => path.instrument === instrumentsMap[key].instrument,
      );
      if (key === 'acoustic-guitar' || key === 'electric-guitar') {
        sortGenres(instrumentsMap[key].paths);
      }
    });
  }
  return instrumentsMap;
}

/**
 * Takes an SHA256 array buffer and converts it to a hex encoded string
 *
 * @param buffer - An ArrayBuffer usually resulting from a digest function
 * @returns Buffer converted to string
 */
function sha256BufferToString(buffer: ArrayBuffer): string {
  const byteArray = new Uint8Array(buffer);
  const hexCodes = [...byteArray].map(value => {
    const hexCode = value.toString(16);
    const paddedHexCode = hexCode.padStart(2, '0');
    return paddedHexCode;
  });
  return hexCodes.join('');
}

/**
 * Creates a hash of a given string (SHA-256)
 *
 * @param input - Input to hash
 * @returns An SHA-256 hash
 */
async function hashSHA256(input: string): Promise<ArrayBuffer> {
  // eslint-disable-next-line
  // @ts-ignore
  const msgBuffer = new TextEncoder('utf-8').encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  return hashBuffer;
}

type TrackingFields = {
  email: string;
  orderId: string;
  amount: number;
  goal: string;
};

type HashFn = (input: string) => Promise<ArrayBuffer>;
type BufferConverter = (buffer: ArrayBuffer) => string;

/**
 * Loads a tracking script used on the quiz route.
 * Third party Bounce script comes from:
 * https://fenderdigital.atlassian.net/browse/PT-2081
 *
 * @param trackingFields - Fields to be tracked by the bounceX pixel
 * @param trackingFields.email - The email to be used as a hash for tracking purposes
 * @param trackingFields.orderId - Field that maps to order_id, order identifier
 * @param trackingFields.amount - The amount to be used
 * @param trackingFields.goal - Campaign goal, usually subscription_purchase
 * @param hashFunction - A function used to hash the email
 * @param bufferConverter - Convert the hash to a string
 */
async function loadScript(
  trackingFields: TrackingFields,
  hashFunction: HashFn,
  bufferConverter: BufferConverter,
): Promise<void> {
  const { email = '', orderId, amount, goal } = trackingFields;
  const bounceWebsiteId = 3447;
  const emailSha256Buffer = await hashFunction(email.trim().toLowerCase());
  const emailSha256 = bufferConverter(emailSha256Buffer);
  window.top.bouncex = window.top.bouncex || [];
  window.top.bouncex.push([
    'conversion',
    {
      email_sha256: emailSha256,
      order_id: orderId,
      amount,
      goal,
    },
  ]);
  const pixel = document.createElement('img');
  pixel.height = 1;
  pixel.width = 1;
  pixel.style.display = 'none';
  pixel.style.border = '0';
  pixel.id = 'bounce-pixel';
  pixel.src =
    `//api.bounceexchange.com/capture/convert2.gif?` +
    `website_id=${bounceWebsiteId}` +
    `&email_sha256=${emailSha256}` +
    `&order_id=${orderId}` +
    `&amount=${amount}` +
    `&goal=${goal}`;
  document.body.appendChild(pixel);
}

export type TrackingObj = {
  email?: string;
  instrument: string | null;
  type?: string | null;
  style: string | null;
  userID: string;
};

/**
 * Segment tracking event for setting initial path.
 *
 * We will eventually move to avo events, and calling
 * this event may become obsolete / or need to be removed.
 *
 * @deprecated
 * @param obj - tracking object.
 */
function trackEvent(obj: TrackingObj): void {
  const { instrument, style, type, userID } = obj;
  segment.event('Initial Path', {
    instrument,
    instrumentType: type,
    style,
    fcId: userID,
    siteId: `${process.env.REACT_APP_CLIENT_SITE_ID}`,
  });
}

function trackChangeEvent(obj: TrackingObj, isOnboarding = true): void {
  const { instrument, style: genre, userID } = obj;
  /* eslint-disable @typescript-eslint/camelcase */
  const trackingProperties = {
    set_during_onboarding: isOnboarding,
    instrument,
    genre,
  };

  const legacyProperties = {
    fcId: userID,
  };

  const userProperties = {
    play_path_level: 1,
    play_path_instrument: instrument,
    play_path_genre: genre,
  };
  /* eslint-enable @typescript-eslint/camelcase */

  trackEventAndUpdate(
    userID,
    EventNames.PATH_CHANGED,
    trackingProperties,
    userProperties,
    legacyProperties,
  );
}

function trackPageEvent(pageEventName: string): void {
  segment.pageTrack('', pageEventName);
}

function trackClickEvent(eventName: string, instrument: string, genre?: string): void {
  segment.event(eventName, {
    ...(genre && { genre }),
    instrument,
  });
}

export {
  getInstruments,
  getInstrumentsStyleImage,
  getInstrumentType,
  loadScript,
  trackEvent,
  trackChangeEvent,
  trackClickEvent,
  trackPageEvent,
  sha256BufferToString,
  hashSHA256,
  sortGenres,
};
